import { useCallback, useEffect, useRef, useState } from 'react'
import {
    getEcommerceLocations,
    getErpLocations,
    getLocationMappings,
    saveLocationMappings
} from './multiLocationApi'

export const createRow = (rowId, mapping) => ({
    rowId,
    erpLocationId: (mapping && mapping.erpLocationId) || '',
    erpMethod: (mapping && mapping.erpMethod) || '',
    ecommerceLocationId: (mapping && mapping.ecommerceLocationId) || '',
    ecommerceMethod: (mapping && mapping.ecommerceMethod) || '',
    isDefault: Boolean(mapping && mapping.isDefault),
    isNew: Boolean(mapping && mapping.isNew)
})

/** A location is identified by its id, so a row without both ids cannot be saved. */
export const isRowComplete = (row) => row.erpLocationId !== '' && row.ecommerceLocationId !== ''

/**
 * Owns the multi location state: both warehouse lists, the editable rows and the
 * load/save lifecycle. Components below only render what this returns.
 */
const useMultiLocationMapping = (connectorId, instancesId, targetId) => {
    const [erpLocations, setErpLocations] = useState([])
    const [ecommerceLocations, setEcommerceLocations] = useState([])
    const [rows, setRows] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const [saveStatus, setSaveStatus] = useState(null)

    const nextRowId = useRef(0)
    const isMounted = useRef(true)
    const loadedKey = useRef(null)

    useEffect(() => {
        isMounted.current = true
        return () => {
            isMounted.current = false
        }
    }, [])

    useEffect(() => {
        if (!connectorId || !instancesId) {
            setIsLoading(false)
            return
        }

        // React 18 StrictMode runs effects twice in development. This ref survives
        // that simulated remount, so each connector/instance is fetched exactly
        // once - no duplicate or cancelled requests in the network tab.
        const key = `${connectorId}|${instancesId}|${targetId}`
        if (loadedKey.current === key) {
            return
        }
        loadedKey.current = key

        setIsLoading(true)
        setLoadError('')

        Promise.all([
            getErpLocations(instancesId, targetId),
            getEcommerceLocations(instancesId, targetId),
            getLocationMappings(connectorId, instancesId)
        ])
            .then(([erpOptions, ecommerceOptions, mappings]) => {
                if (!isMounted.current) {
                    return
                }
                setErpLocations(erpOptions)
                setEcommerceLocations(ecommerceOptions)
                setRows(mappings.map((mapping) => {
                    nextRowId.current += 1
                    return createRow(nextRowId.current, mapping)
                }))
                setIsLoading(false)
            })
            .catch((error) => {
                if (!isMounted.current) {
                    return
                }
                setLoadError('Unable to load the location details. Please try again.')
                setIsLoading(false)
                // eslint-disable-next-line no-console
                console.error('Multi location mapping load failed', error)
            })
    }, [connectorId, instancesId, targetId])

    const addRow = useCallback((mapping) => {
        setSaveStatus(null)
        setRows((current) => {
            nextRowId.current += 1
            return [createRow(nextRowId.current, { ...mapping, isNew: true }), ...current]
        })
    }, [])

    const updateRow = useCallback((rowId, changes) => {
        setSaveStatus(null)
        setRows((current) => current.map((row) => (row.rowId === rowId ? { ...row, ...changes } : row)))
    }, [])

    const removeRow = useCallback((rowId) => {
        setSaveStatus(null)
        setRows((current) => current.filter((row) => row.rowId !== rowId))
    }, [])

    const submitMappings = useCallback(() => {
        const payload = rows.filter(isRowComplete)

        setIsSaving(true)
        setSaveStatus(null)

        return saveLocationMappings(connectorId, instancesId, payload)
            .then(() => {
                if (!isMounted.current) {
                    return
                }
                setRows((current) => current.map((row) => ({ ...row, isNew: false })))
                setSaveStatus({
                    result: true,
                    message: `${payload.length} location mapping${payload.length === 1 ? '' : 's'} saved successfully.`
                })
            })
            .catch((error) => {
                if (!isMounted.current) {
                    return
                }
                setSaveStatus({ result: false, message: 'Unable to save the location mappings.' })
                // eslint-disable-next-line no-console
                console.error('Multi location mapping save failed', error)
            })
            .finally(() => {
                if (isMounted.current) {
                    setIsSaving(false)
                }
            })
    }, [rows, connectorId, instancesId])

    const dismissStatus = useCallback(() => setSaveStatus(null), [])

    return {
        erpLocations,
        ecommerceLocations,
        rows,
        isLoading,
        loadError,
        isSaving,
        saveStatus,
        addRow,
        updateRow,
        removeRow,
        submitMappings,
        dismissStatus
    }
}

export default useMultiLocationMapping
